/**
 * データ整合 ＞「システムデータ整合」タブ — 2026-07-28
 *
 * 予約データ（sys/backend）そのものを対象にした整合作業。3機能:
 *   1. データ編集  … コンテキストウィンドウ（自然言語）→ 抽出 → チェックで選択 → 一括編集
 *   2. データバックアップ … 予約の完全スナップショット（JSON）を保存 / DL
 *   3. データ復元  … バックアップから書き戻す
 *
 * ■ 現在のフェーズ（Phase A / 読み取り系のみ）
 *   抽出・リスト表示・バックアップ・**差分プレビュー（ドライラン）** までを実装。
 *   一括編集の実行と復元の実行は Phase B（ユーザー確認後）で有効化する。
 *   → 本番の予約データに対して、この画面はまだ 1 バイトも書き込まない。
 *
 * ■ 安全設計（ref-incidents.md の 580 件破損事象を踏まえる）
 *   - AI は「条件」しか作らない。抽出も件数も queryEngine.ts が決定的に行う
 *     （agent-rules.md #1 捏造禁止を構造で担保）
 *   - 書き込み可能フィールドは fields.ts のホワイトリスト。価格系は第1弾では書き換え禁止
 *   - 実行は必ず 差分プレビュー →（件数上限 200）→ 自動バックアップ成功 → 明示確認 の4段
 *   - 保存は 1件ずつ fresh-fetch → `{...fresh, detail:{...fresh.detail, 差分}, __completeDetail:true}`
 *     （ref-save-reservation.md の唯一安全なパターン。sparse detail 500 と detail=null 予約を同時に回避）
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Checkbox, Chip, TextField, CircularProgress, Button, Alert, Dialog, DialogTitle,
    DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel, FormControlLabel,
    Switch, Tooltip, Divider, LinearProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import BackupIcon from '@mui/icons-material/Backup';
import RestoreIcon from '@mui/icons-material/Restore';
import PreviewIcon from '@mui/icons-material/Preview';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import { fetchDispatchReservations } from '../../api/dispatchService';
import { EDITABLE_FIELDS, FIELD_DEFS, fieldLabel, getField } from './fields';
import {
    EditOp, EditPlanRow, QuerySpec, buildEditPlan, describeEdit, describeQuery,
    filterReservations, formatValue, parseEditOp, parseQuerySpec, readField,
} from './queryEngine';
import {
    BackupMeta, downloadBackupCsv, downloadBackupJson, fileStamp, getBackup, getOperatorName,
    listBackups, requestPlan, saveBackup,
} from './systemApi';

/** 一括編集の1回あたり上限（安全機構②）。超えたら分割してもらう */
const MAX_BULK = 200;
/** 抽出結果の表示上限。超過分は件数だけ伝えて「条件を絞ってください」と促す */
const MAX_ROWS = 1000;

interface ChatMsg {
    role: 'user' | 'assistant';
    text: string;
    chips?: string[];
}

const toDateInput = (d: Date): string => {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const shiftDays = (days: number): string => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return toDateInput(d);
};

const SystemTab: React.FC = () => {
    // ---- 既定の取得範囲（AI が日付を指定しなかったときに使う） ----
    const [rangeFrom, setRangeFrom] = useState<string>(shiftDays(-90));
    const [rangeTo, setRangeTo] = useState<string>(shiftDays(90));

    // ---- 対話 ----
    const [messages, setMessages] = useState<ChatMsg[]>([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);

    // ---- 抽出結果 ----
    const [spec, setSpec] = useState<QuerySpec | null>(null);
    const [effectiveRange, setEffectiveRange] = useState<{ from: string; to: string } | null>(null);
    const [results, setResults] = useState<any[]>([]);
    const [truncated, setTruncated] = useState(0);
    const [selected, setSelected] = useState<Set<string>>(new Set());

    // ---- 一括編集（プレビューまで） ----
    const [editField, setEditField] = useState<string>(EDITABLE_FIELDS[0]?.key || '');
    const [editMode, setEditMode] = useState<'set' | 'copy'>('set');
    const [editValue, setEditValue] = useState('');
    const [editFrom, setEditFrom] = useState<string>('base_price');
    const [onlyWhenEmpty, setOnlyWhenEmpty] = useState(true);
    const [preview, setPreview] = useState<{ rows: EditPlanRow[]; edit: EditOp } | null>(null);

    // ---- バックアップ ----
    const [backupOpen, setBackupOpen] = useState(false);
    const [backups, setBackups] = useState<BackupMeta[]>([]);
    const [backupBusy, setBackupBusy] = useState(false);
    const [notice, setNotice] = useState<{ severity: 'success' | 'info' | 'warning' | 'error'; text: string } | null>(null);

    const scrollChat = () => setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

    const pushAssistant = (text: string, chips?: string[]) => {
        setMessages((prev) => [...prev, { role: 'assistant', text, chips }]);
        scrollChat();
    };

    // ============================================================
    // 抽出（AI は条件だけ作る / 実データはここで決定的に処理する）
    // ============================================================
    const runQuery = useCallback(async (querySpec: QuerySpec) => {
        const from = querySpec.dateFrom || rangeFrom;
        const to = querySpec.dateTo || rangeTo;
        setEffectiveRange({ from, to });

        const all = await fetchDispatchReservations({ start_date: from, end_date: to });
        const hits = filterReservations(all, { ...querySpec, dateFrom: from, dateTo: to });

        setSpec(querySpec);
        setSelected(new Set());
        setTruncated(Math.max(0, hits.length - MAX_ROWS));
        setResults(hits.slice(0, MAX_ROWS));
        return hits.length;
    }, [rangeFrom, rangeTo]);

    const send = async () => {
        const text = input.trim();
        if (!text || sending) return;
        setInput('');
        const next: ChatMsg[] = [...messages, { role: 'user', text }];
        setMessages(next);
        setSending(true);
        scrollChat();
        try {
            const plan = await requestPlan(next.map((m) => ({ role: m.role, text: m.text })));
            const querySpec = parseQuerySpec(plan.query);
            const editOp = parseEditOp(plan.edit);

            if (!querySpec) {
                // 条件に落とせなかった / 対応できない依頼
                const why = plan.message || '指示を抽出条件に変換できませんでした。';
                const priceAsked = plan.edit && !editOp && getField((plan.edit as any).field ?? (plan.edit as any).to)?.price;
                pushAssistant(priceAsked
                    ? `${why}\n価格系（合計金額・基本料金など）の一括編集は第1弾では無効です。抽出だけなら行えます。`
                    : why);
                return;
            }

            const total = await runQuery(querySpec);
            const chips = describeQuery(querySpec);
            let reply = `${total} 件見つかりました。`;
            if (total > MAX_ROWS) reply += `（表示は上位 ${MAX_ROWS} 件。条件を絞ってください）`;

            if (editOp) {
                setEditMode(editOp.type);
                setEditField(editOp.field);
                setOnlyWhenEmpty(editOp.onlyWhenEmpty);
                if (editOp.type === 'set') setEditValue(editOp.value);
                else setEditFrom(editOp.from);
                reply += `\n編集操作の下書きを作りました: ${describeEdit(editOp)}\n対象を選んで「差分プレビュー」で確認してください。`;
            } else if (plan.edit && !editOp) {
                reply += '\n※ 依頼された書き換えは対象フィールド外のため、抽出のみ行いました。';
            }
            pushAssistant(reply, chips);
        } catch (e: any) {
            pushAssistant(`エラー: ${e?.message || e}`);
        } finally {
            setSending(false);
        }
    };

    // ============================================================
    // 選択
    // ============================================================
    const toggleRow = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        setSelected((prev) => (prev.size === results.length
            ? new Set<string>()
            : new Set(results.map((r) => String(r.id)))));
    };

    const selectedRows = useMemo(
        () => results.filter((r) => selected.has(String(r.id))),
        [results, selected],
    );

    /** 条件に使ったフィールドを結果テーブルの列として出す（何を見て絞ったかが一目でわかる） */
    const extraColumns = useMemo(() => {
        const keys = Array.from(new Set((spec?.filters || []).map((f) => f.field)));
        return keys.map((k) => getField(k)).filter(Boolean) as NonNullable<ReturnType<typeof getField>>[];
    }, [spec]);

    // ============================================================
    // バックアップ
    // ============================================================
    const doBackup = async (rows: any[], label: string) => {
        if (rows.length === 0) {
            setNotice({ severity: 'warning', text: 'バックアップ対象がありません。先に抽出して選択してください。' });
            return;
        }
        setBackupBusy(true);
        setNotice(null);
        try {
            const meta = {
                query: spec ? describeQuery(spec) : [],
                range: effectiveRange,
                created_by: getOperatorName() || '(対応者未設定)',
            };
            const saved = await saveBackup(rows, { label, source: 'manual', meta });
            const stamp = fileStamp();
            downloadBackupJson(rows, { ...meta, backup_id: saved.backup_id, label }, `reservations-backup-${stamp}.json`);
            downloadBackupCsv(rows, `reservations-backup-${stamp}.csv`);
            setNotice({
                severity: 'success',
                text: `バックアップを保存しました（ID: ${saved.backup_id} / ${saved.count} 件 / ${Math.round(saved.bytes / 1024)} KB）。JSON と CSV を手元にもダウンロードしました。復元に使えるのは JSON です。`,
            });
        } catch (e: any) {
            setNotice({ severity: 'error', text: `バックアップに失敗しました: ${e?.message || e}` });
        } finally {
            setBackupBusy(false);
        }
    };

    const openBackupList = async () => {
        setBackupOpen(true);
        setBackupBusy(true);
        try {
            const { backups: list } = await listBackups();
            setBackups(list);
        } catch (e: any) {
            setNotice({ severity: 'error', text: `バックアップ一覧の取得に失敗しました: ${e?.message || e}` });
        } finally {
            setBackupBusy(false);
        }
    };

    // ============================================================
    // 差分プレビュー（ドライラン）
    // ============================================================
    const buildPreview = () => {
        const target = getField(editField);
        if (!target?.editable) {
            setNotice({ severity: 'error', text: 'そのフィールドは書き換え対象外です。' });
            return;
        }
        if (selectedRows.length === 0) {
            setNotice({ severity: 'warning', text: '対象の予約にチェックを入れてください。' });
            return;
        }
        const edit: EditOp = editMode === 'set'
            ? { type: 'set', field: editField, value: editValue, onlyWhenEmpty }
            : { type: 'copy', field: editField, from: editFrom, onlyWhenEmpty };
        setPreview({ rows: buildEditPlan(selectedRows, edit), edit });
    };

    const changedRows = preview ? preview.rows.filter((r) => r.willChange) : [];
    const overLimit = changedRows.length > MAX_BULK;

    // ============================================================
    // 描画
    // ============================================================
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, gap: 1.5 }}>
            <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ py: 0.5 }}>
                <strong>現在は読み取り専用フェーズです。</strong>
                抽出・バックアップ・差分プレビューまで動作し、<strong>予約データへの書き込みは行いません</strong>。
                一括編集の実行と復元は、この画面の内容を確認いただいたあとで有効化します。
            </Alert>

            {notice && (
                <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ py: 0.5 }}>
                    {notice.text}
                </Alert>
            )}

            {/* ツールバー */}
            <Paper elevation={1} sx={{ borderRadius: 3, p: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>既定の取得範囲</Typography>
                <TextField
                    type="date" size="small" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)}
                    sx={{ width: 160 }} InputLabelProps={{ shrink: true }} label="貸出日 から"
                />
                <TextField
                    type="date" size="small" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)}
                    sx={{ width: 160 }} InputLabelProps={{ shrink: true }} label="貸出日 まで"
                />
                <Tooltip title="指示に日付が含まれていればそちらが優先されます">
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        指示に日付が無いときに使う範囲です
                    </Typography>
                </Tooltip>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                    size="small" variant="outlined" startIcon={<BackupIcon />} disabled={backupBusy}
                    onClick={() => doBackup(selectedRows.length > 0 ? selectedRows : results, selectedRows.length > 0 ? '選択した予約の手動バックアップ' : '抽出結果の手動バックアップ')}
                >
                    データバックアップ{selectedRows.length > 0 ? `（選択 ${selectedRows.length} 件）` : results.length > 0 ? `（抽出 ${results.length} 件）` : ''}
                </Button>
                <Button size="small" variant="outlined" startIcon={<RestoreIcon />} onClick={openBackupList}>
                    データ復元
                </Button>
            </Paper>

            {/* 抽出条件 */}
            {spec && (
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>抽出条件:</Typography>
                    {describeQuery(spec).map((c) => (
                        <Chip key={c} size="small" label={c} color="primary" variant="outlined" sx={{ height: 22 }} />
                    ))}
                    {effectiveRange && (
                        <Chip size="small" label={`取得範囲 ${effectiveRange.from} 〜 ${effectiveRange.to}`} sx={{ height: 22 }} />
                    )}
                    <Chip size="small" color="success" label={`該当 ${results.length + truncated} 件`} sx={{ height: 22, fontWeight: 700 }} />
                    {truncated > 0 && <Chip size="small" color="warning" label={`表示は上位 ${MAX_ROWS} 件`} sx={{ height: 22 }} />}
                </Box>
            )}

            {/* 結果テーブル */}
            <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3, flexGrow: 1, minHeight: 180 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    size="small"
                                    checked={results.length > 0 && selected.size === results.length}
                                    indeterminate={selected.size > 0 && selected.size < results.length}
                                    onChange={toggleAll}
                                    disabled={results.length === 0}
                                />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>予約ID</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>予約番号</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>貸出日</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>予約者名</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>車種</TableCell>
                            {extraColumns.map((c) => (
                                <TableCell key={c.key} sx={{ fontWeight: 700 }}>{c.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6 + extraColumns.length} sx={{ color: '#94a3b8', py: 4, textAlign: 'center' }}>
                                    下のコンテキストウィンドウに指示を入力すると、該当する予約がここに一覧表示されます。
                                </TableCell>
                            </TableRow>
                        )}
                        {results.map((r) => {
                            const id = String(r.id);
                            return (
                                <TableRow key={id} hover selected={selected.has(id)}>
                                    <TableCell padding="checkbox">
                                        <Checkbox size="small" checked={selected.has(id)} onChange={() => toggleRow(id)} />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{id}</TableCell>
                                    <TableCell>{r.contact_code || r.detail?.contact_id || '—'}</TableCell>
                                    <TableCell>{String(r.start_date || '').slice(0, 10) || '—'}</TableCell>
                                    <TableCell>{r.customer_name || '—'}</TableCell>
                                    <TableCell>{r.car_type || '—'}</TableCell>
                                    {extraColumns.map((c) => (
                                        <TableCell key={c.key}>{formatValue(readField(r, c.key), c.kind)}</TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* 一括編集（プレビューまで） */}
            <Paper elevation={1} sx={{ borderRadius: 3, p: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                    一括編集（選択 {selectedRows.length} 件）
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>書き込み先</InputLabel>
                    <Select label="書き込み先" value={editField} onChange={(e) => setEditField(String(e.target.value))}>
                        {EDITABLE_FIELDS.map((f) => (
                            <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>操作</InputLabel>
                    <Select label="操作" value={editMode} onChange={(e) => setEditMode(e.target.value as 'set' | 'copy')}>
                        <MenuItem value="set">値を設定</MenuItem>
                        <MenuItem value="copy">他の項目から複製</MenuItem>
                    </Select>
                </FormControl>
                {editMode === 'set' ? (
                    <TextField
                        size="small" label="設定する値" value={editValue}
                        onChange={(e) => setEditValue(e.target.value)} sx={{ minWidth: 180 }}
                    />
                ) : (
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel>複製元</InputLabel>
                        <Select label="複製元" value={editFrom} onChange={(e) => setEditFrom(String(e.target.value))}>
                            {FIELD_DEFS.filter((f) => f.key !== editField).map((f) => (
                                <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <FormControlLabel
                    control={<Switch size="small" checked={onlyWhenEmpty} onChange={(e) => setOnlyWhenEmpty(e.target.checked)} />}
                    label={<Typography variant="body2">未入力のときだけ書き込む</Typography>}
                />
                <Button
                    size="small" variant="contained" startIcon={<PreviewIcon />}
                    onClick={buildPreview} disabled={selectedRows.length === 0}
                >
                    差分プレビュー
                </Button>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    価格系（合計金額・基本料金など）は第1弾では書き換え対象外です
                </Typography>
            </Paper>

            {/* コンテキストウィンドウ */}
            <Paper elevation={2} sx={{ borderRadius: 3, p: 2, display: 'flex', flexDirection: 'column', maxHeight: '30vh' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 1 }}>
                    コンテキストウィンドウ（例: 「7/1以降で予約経路がないものをリストアップして」「合計金額がない予約を抽出して」）
                </Typography>
                <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 70, mb: 1 }}>
                    {messages.map((m, i) => (
                        <Box key={i} sx={{ mb: 1, textAlign: m.role === 'user' ? 'right' : 'left' }}>
                            {m.role === 'assistant' && (m.chips || []).map((c) => (
                                <Chip key={c} size="small" label={c} sx={{ mr: 0.5, mb: 0.5, height: 20, fontSize: 11, bgcolor: '#e0f2fe' }} />
                            ))}
                            <Paper elevation={0} sx={{
                                display: 'inline-block', px: 1.5, py: 1, borderRadius: 2, maxWidth: '85%',
                                bgcolor: m.role === 'user' ? '#0ea5e9' : '#f1f5f9',
                                color: m.role === 'user' ? '#fff' : '#1e293b',
                                whiteSpace: 'pre-wrap', textAlign: 'left', fontSize: 14,
                            }}>
                                {m.text}
                            </Paper>
                        </Box>
                    ))}
                    {sending && <CircularProgress size={18} sx={{ ml: 1 }} />}
                    <div ref={chatEndRef} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth size="small" placeholder="抽出したい条件を入力（Enterで送信）"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
                        disabled={sending}
                    />
                    <Button variant="contained" onClick={send} disabled={sending || !input.trim()} endIcon={<SendIcon />}>
                        送信
                    </Button>
                </Box>
            </Paper>

            {/* 差分プレビュー ダイアログ */}
            <Dialog open={preview !== null} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>
                    差分プレビュー（ドライラン）
                </DialogTitle>
                <DialogContent dividers>
                    {preview && (
                        <>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
                                <Chip size="small" color="primary" label={describeEdit(preview.edit)} />
                                <Chip size="small" label={`選択 ${preview.rows.length} 件`} />
                                <Chip size="small" color={changedRows.length > 0 ? 'success' : 'default'} label={`実際に変わる ${changedRows.length} 件`} />
                                <Chip size="small" label={`変更なし ${preview.rows.length - changedRows.length} 件`} />
                            </Box>
                            {overLimit && (
                                <Alert severity="warning" sx={{ mb: 1.5 }}>
                                    1回の実行は {MAX_BULK} 件までです。条件を絞るか、選択を分割してください。
                                </Alert>
                            )}
                            {changedRows.length === 0 && (
                                <Alert severity="info" sx={{ mb: 1.5 }}>
                                    値が変わる予約はありません。実行しても何も起きません。
                                </Alert>
                            )}
                            <TableContainer sx={{ maxHeight: 380 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>予約ID</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>予約番号</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>貸出日</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>予約者名</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>{fieldLabel(preview.edit.field)}（現在）</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>→ 変更後</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {preview.rows.map((row) => (
                                            <TableRow key={String(row.id)} sx={{ bgcolor: row.willChange ? '#f0fdf4' : undefined }}>
                                                <TableCell sx={{ fontWeight: 600 }}>{row.id}</TableCell>
                                                <TableCell>{row.contactCode || '—'}</TableCell>
                                                <TableCell>{row.startDate || '—'}</TableCell>
                                                <TableCell>{row.customerName || '—'}</TableCell>
                                                <TableCell sx={{ color: '#64748b' }}>
                                                    {formatValue(row.before, getField(preview.edit.field)?.kind || 'string')}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: row.willChange ? 700 : 400, color: row.willChange ? '#166534' : '#94a3b8' }}>
                                                    {row.willChange
                                                        ? formatValue(row.after, getField(preview.edit.field)?.kind || 'string')
                                                        : `変更しない（${row.skipReason}）`}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', maxWidth: '60%' }}>
                        実行を有効化すると、直前に自動バックアップを取り（成功が実行の前提）、1件ずつ最新データを取り直して保存します。
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => setPreview(null)}>閉じる</Button>
                        <Tooltip title="読み取り専用フェーズのため無効です。抽出・プレビューの内容を確認後に有効化します">
                            <span>
                                <Button variant="contained" color="error" disabled startIcon={<LockIcon />}>
                                    実行（次フェーズで有効化）
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* バックアップ一覧 / 復元 ダイアログ */}
            <Dialog open={backupOpen} onClose={() => setBackupOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>バックアップ一覧 / データ復元</DialogTitle>
                <DialogContent dividers>
                    {backupBusy && <LinearProgress sx={{ mb: 1.5 }} />}
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                        バックアップは <strong>JSON（完全スナップショット）</strong>としてサーバに保存され、90日で自動削除されます。
                        CSV は閲覧用の副産物で、復元には使いません（detail のネスト構造が保持できないため）。
                    </Alert>
                    <Divider sx={{ mb: 1.5 }} />
                    <TableContainer sx={{ maxHeight: 360 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>作成日時</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>ラベル</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>種別</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>対応者</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="right">件数</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {backups.length === 0 && !backupBusy && (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ color: '#94a3b8', py: 4, textAlign: 'center' }}>
                                            まだバックアップがありません。
                                        </TableCell>
                                    </TableRow>
                                )}
                                {backups.map((b) => (
                                    <TableRow key={b.backup_id} hover>
                                        <TableCell>{String(b.created_at || '').replace('T', ' ').slice(0, 16)}</TableCell>
                                        <TableCell>{b.label || '—'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small" sx={{ height: 20 }}
                                                color={b.source === 'auto_before_edit' ? 'warning' : 'default'}
                                                label={b.source === 'auto_before_edit' ? '編集前(自動)' : '手動'}
                                            />
                                        </TableCell>
                                        <TableCell>{b.operator || '—'}</TableCell>
                                        <TableCell align="right">{b.count}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="このバックアップの JSON をダウンロード">
                                                <span>
                                                    <Button
                                                        size="small" startIcon={<DownloadIcon />}
                                                        onClick={async () => {
                                                            try {
                                                                const data = await getBackup(b.backup_id);
                                                                downloadBackupJson(data.reservations, data.meta, `${b.backup_id}.json`);
                                                            } catch (e: any) {
                                                                setNotice({ severity: 'error', text: `取得に失敗しました: ${e?.message || e}` });
                                                            }
                                                        }}
                                                    >
                                                        DL
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="読み取り専用フェーズのため無効です">
                                                <span>
                                                    <Button size="small" color="error" disabled startIcon={<LockIcon />}>
                                                        復元
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBackupOpen(false)}>閉じる</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SystemTab;
